import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronUp, ChevronDown } from "lucide-react";
import Wheel from "./Wheel";
import WheelList from "./WheelList";
import { useShuffle } from "../NavAudioShuffle";
import { playClick } from "../../lib/profileSong";

export function cardRow(it, sub) {
  const kind = it.source === "podcast" ? "Podcast" : it.media_type === "tv" ? "Series" : "Film";
  return {
    key: it.id, id: it.id, label: it.title, img: it.poster || it.img,
    href: it.source === "podcast" ? `/podcast/${it.slug}` : `/title/${it.id}`,
    sub: sub ?? [it.year, kind, it.rating ? `★ ${Number(it.rating).toFixed(1)}` : null].filter(Boolean).join(" · "),
  };
}

// Click Wheel list + wheel for any page of titles. Rows stay directly tappable.
// onMove(index, dir) enables the up/down reorder handle on the selected row.
export default function WheelBrowser({ rows, title, testid, onMove, onHold }) {
  const navigate = useNavigate();
  const shuffle = useShuffle();
  const [sel, setSel] = useState(0);
  const max = Math.max(0, rows.length - 1);
  const cur = Math.min(sel, max);
  const listRef = useRef(null);
  const dockRef = useRef(null);

  const step = (dir) => setSel((s) => {
    const from = Math.min(s, max);
    const next = Math.max(0, Math.min(max, from + dir));
    if (next !== from) playClick("tick");
    return next;
  });
  const pick = (i) => { playClick("select"); if (rows[i]) navigate(rows[i].href); };
  const stepRef = useRef(step);
  stepRef.current = step;
  const pickRef = useRef(() => pick(cur));
  pickRef.current = () => pick(cur);

  useEffect(() => {
    let acc = 0;
    const onWheel = (e) => {
      e.preventDefault();
      acc += e.deltaY;
      while (Math.abs(acc) >= 40) { const dir = acc > 0 ? 1 : -1; acc -= dir * 40; stepRef.current(dir); }
    };
    const onKey = (e) => {
      if (e.target.closest?.("input, textarea, select, [contenteditable]") || document.querySelector("[role=dialog]")) return;
      if (e.key === "ArrowDown") { e.preventDefault(); stepRef.current(1); }
      else if (e.key === "ArrowUp") { e.preventDefault(); stepRef.current(-1); }
      else if (e.key === "Enter" && !e.target.closest?.("button, a")) pickRef.current();
    };
    const els = [listRef.current, dockRef.current].filter(Boolean);
    els.forEach((el) => el.addEventListener("wheel", onWheel, { passive: false }));
    window.addEventListener("keydown", onKey);
    return () => { els.forEach((el) => el.removeEventListener("wheel", onWheel)); window.removeEventListener("keydown", onKey); };
  }, []);

  const move = (dir) => {
    const to = cur + dir;
    if (to < 0 || to > max) return;
    playClick("tick");
    onMove(cur, dir);
    setSel(to);
  };
  const extra = onMove ? (i) => i === cur && (
    <span className="flex flex-none flex-col" data-testid={`${testid}-reorder`}>
      <button type="button" aria-label="Move up" onClick={() => move(-1)} disabled={cur === 0} className="rounded-sm p-0.5 hover:bg-white/20 disabled:opacity-30" data-testid={`${testid}-move-up`}><ChevronUp size={16} /></button>
      <button type="button" aria-label="Move down" onClick={() => move(1)} disabled={cur === max} className="rounded-sm p-0.5 hover:bg-white/20 disabled:opacity-30" data-testid={`${testid}-move-down`}><ChevronDown size={16} /></button>
    </span>
  ) : undefined;

  return (
    <div className="mx-auto mt-6 flex max-w-5xl flex-col" data-testid={testid}>
      <section className="flex flex-col overflow-hidden rounded-[var(--card-radius)] border-2 border-[var(--text-primary)] bg-[var(--bg-card)]" style={{ height: "calc(100vh - 420px)", minHeight: 260 }}>
        <header className="flex items-center gap-2 border-b-2 border-[var(--text-primary)] bg-[var(--bg-surface)] px-3 py-2">
          <h2 className="flex-1 truncate font-head text-sm font-bold uppercase tracking-[0.2em] text-[var(--text-primary)]">{title}</h2>
          <span className="text-[11px] font-semibold text-[var(--text-muted)]" data-testid={`${testid}-position`}>{rows.length ? `${cur + 1} of ${rows.length}` : ""}</span>
        </header>
        <div ref={listRef} className="min-h-0 flex-1">
          <WheelList rows={rows} sel={cur} onHover={setSel} onPick={pick} testid={`${testid}-list`} extra={extra} onHold={onHold ? (i) => { playClick("select"); onHold(i); } : undefined} />
        </div>
      </section>
      <div ref={dockRef} className="sticky bottom-16 z-20 flex justify-center bg-[var(--bg-base)] py-4 md:bottom-0 md:py-5">
        <Wheel onStep={step} onSelect={() => pick(cur)} onSelectHold={onHold ? () => { playClick("select"); onHold(cur); } : undefined} onMenu={() => { playClick("select"); navigate("/"); }} onShuffle={() => { playClick("select"); shuffle(); }} size={170} />
      </div>
    </div>
  );
}
